// Shared types between worker and client

import type { RuntimeEstimate } from './runtime';
import type { ReviewChecklistSetting } from './reviewChecklist';

export type ScriptStatus = 'draft' | 'v1' | 'v2' | 'v3' | 'approved';

export type BundleKind = 'review' | 'shoot';

/** One client-visible item on a bundle hub after readiness filtering. */
export type ResolvedBundleItem =
  | {
      kind: 'script';
      slug: string;
      title: string;
      status: ScriptStatus;
      /** Which kind of document the card opens: a client sees a small
       *  "Interview brief" tag on a brief, nothing on an ordinary script. */
      template_key: TemplateKey;
      approved_at: number | null;
      feedback_submitted_at: number | null;
      /** On-camera runtime estimate, computed server-side from the script's
       *  doc_json (see resolveBundleItems in shared/bundle.ts). Null for a
       *  near-empty script (below the word threshold), same as the editor's
       *  own RuntimeBadge. */
      runtime: RuntimeEstimate | null;
    }
  | { kind: 'coming_soon'; title: string };

/** GET /api/client/bundles/:slug success payload (Phase 3 client hub). */
export interface ClientBundle {
  title: string;
  client_name: string | null;
  kind: BundleKind;
  intro_note: string | null;
  items: ResolvedBundleItem[];
}

// ---------- Admin bundle ("Links") API shapes ----------
// Mirror the JSON built by src/worker/routes/admin.ts's bundle routes.
// Named with an Admin* prefix to keep them distinct from ResolvedBundleItem
// (the client hub's post-readiness-filter shape) and from worker/lib/db.ts's
// internal BundleDetail (raw DB rows).

export interface AdminBundleSummary {
  id: string;
  slug: string;
  title: string;
  client_name: string | null;
  kind: BundleKind;
  archived_at: number | null;
  ready_emailed_at: number | null;
  created_at: number;
  updated_at: number;
  item_count: number;
  ready_count: number;
  approved_count: number;
}

export interface AdminBundleItem {
  id: string;
  script_id: string | null;
  placeholder_title: string | null;
  position: number;
  show_before_ready: boolean;
  script: { id: string; slug: string; title: string; status: ScriptStatus } | null;
  /** Human-readable copy when the referenced script is missing, trashed, or
   *  archived — render verbatim as a warning chip on the item row. */
  warning: string | null;
}

export interface AdminBundleDetail {
  id: string;
  slug: string;
  title: string;
  client_name: string | null;
  kind: BundleKind;
  intro_note: string | null;
  client_email: string | null;
  ready_emailed_at: number | null;
  archived_at: number | null;
  created_at: number;
  updated_at: number;
  items: AdminBundleItem[];
}

/** Body accepted by POST /api/admin/bundles and PATCH /api/admin/bundles/:id
 *  for the ordered item list — position is implied by array order. */
export interface AdminBundleItemInput {
  script_id?: string | null;
  placeholder_title?: string | null;
  show_before_ready: boolean;
}

export const SCRIPT_STATUSES: ScriptStatus[] = ['draft', 'v1', 'v2', 'v3', 'approved'];

export const STATUS_LABELS: Record<ScriptStatus, string> = {
  draft: 'Draft',
  v1: 'V1',
  v2: 'V2',
  v3: 'V3',
  approved: 'Approved',
};

export function isStatusShareable(status: ScriptStatus): boolean {
  return status !== 'draft';
}
export type ProjectKind = 'single' | 'campaign';
export type TemplateKey =
  | 'brand_film'
  | 'testimonial'
  | 'explainer'
  | 'social_short'
  | 'fb_ad_campaign'
  | 'interview_brief';

export interface ScriptSummary {
  id: string;
  slug: string;
  title: string;
  status: ScriptStatus;
  template_key: TemplateKey;
  project_id: string;
  project_name: string;
  client_name: string;
  doc_version: number;
  updated_at: number;
  open_comment_count: number;
  pending_suggestion_count: number;
  last_viewed_at: number | null;
  feedback_submitted_at: number | null;
  feedback_submitted_by: string;
  archived_at: number | null;
  last_heartbeat_at: number | null;
  deleted_at: number | null;
}

export interface Script extends ScriptSummary {
  doc_json: string; // serialized ProseMirror JSON
  features: ScriptFeatures;
  /** Per-script "What we need from you" checklist mode and custom prompts
   *  (C8). Template mode with no prompts when never set. */
  review_checklist: ReviewChecklistSetting;
  /** Slug used to identify the parent project in the GHL-driven client
   *  dashboard. Null when the project was created without a slug. Stored
   *  on the project, not the script, so editing it from one script will
   *  change it for every script that shares the same project. */
  dashboard_slug: string | null;
  /** Optional client contact email for the "ready to review" publish nudge
   *  (Phase B3) — script-scoped, unlike dashboard_slug above. Null means
   *  the nudge is skipped when this script is published. */
  client_email: string | null;
  /** Target approval date (plain ISO YYYY-MM-DD) for the review-page deadline
   *  framing, or null when Henry hasn't set one. */
  approve_by: string | null;
  /** Admin-only scratchpad (see shared/scratch.ts). Serialized JSON or null
   *  when nothing has been jotted yet. NEVER on PublicScript: the client
   *  payload is an allowlist and a test asserts no scratch keys leak. */
  scratch_json: string | null;
  /** Optimistic-concurrency counter for the scratchpad, independent of
   *  doc_version so a stale scratch save never blocks a script save. */
  scratch_version: number;
  /** Whether the scratch pane was left open on this script. */
  scratch_open: boolean;
  // ---- Publish revisions (2A). doc_json above is the working copy. ----
  published_round: number | null;
  published_at: number | null;
  published_version_id: string | null;
  published_doc_version: number | null;
  published_title: string | null;
  published_note: string;
  round_published_at: number | null;
  last_client_feedback_at: number | null;
  last_client_feedback_by: string;
  last_reminded_at: number | null;
  approved_at: number | null;
  approved_by_name: string | null;
  round_summary: string | null;
  /** Client emails can be sent (Resend configured). */
  email_enabled: boolean;
  /** Working copy against what the client reads. */
  unpublished: 'none' | 'internal' | 'client';
}

export interface PublicScript {
  slug: string;
  title: string;
  status: ScriptStatus;
  /** Which kind of document this is. The client page uses it to switch the
   *  eyebrow, copy and PDF between a script and an interview brief. */
  template_key: TemplateKey;
  client_name: string;
  project_name: string;
  doc_json: string;
  /** Version of the doc this payload was built from. Compared against the
   *  comment poll's script_doc_version to detect a stale open tab. */
  doc_version: number;
  approved_at: number | null;
  features: ScriptFeatures;
  /** The "What we need from you" prompts (C8), already resolved on the
   *  server: the per-script override, else the template default. Null when
   *  off, outside v1 to v3, or a brief that isn't switched on. The client
   *  never sees modes. */
  review_checklist: string[] | null;
  feedback_submitted_at: number | null;
  feedback_submitted_by: string;
  /** Target approval date (plain ISO YYYY-MM-DD) for the deadline framing, or
   *  null when Henry hasn't set one. */
  approve_by: string | null;
  /** The client's dashboard slug — used to render the "track on your dashboard"
   *  pointer (Feature J). Null when the project isn't linked to a dashboard.
   *  Safe to expose: it's the client's own dashboard, slug-gated like this page. */
  dashboard_slug: string | null;
  /** The name the client typed to sign off, once approved. Null before approval
   *  and for admin-side approvals. */
  approved_by_name: string | null;
  /** The once-per-round "what changed" summary JSON (Feature H), or null when
   *  none was generated for the current round. */
  round_summary: string | null;
  published_round: number | null;
  published_at: number | null;
  published_version_id: string | null;
  /** Last quiet correction after approval, when accompanied by a fix note. */
  tidied: { at: number; note: string } | null;
}

export interface SaveResult {
  ok: true;
  doc_version: number;
  updated_at: number;
  /** 2A: working copy against the published copy after this save. */
  unpublished?: 'none' | 'internal' | 'client';
}

export interface SaveStaleError {
  ok: false;
  error: 'stale';
  current_version: number;
}

export interface ApiError {
  ok: false;
  error: string;
}

/** POST /api/admin/scripts/:id/scratch success. */
export interface ScratchSaveResult {
  ok: true;
  version: number;
}

// ---------- Comments ----------

export type ThreadStatus = 'open' | 'resolved' | 'orphaned';
export type AuthorKind = 'client' | 'admin';

export interface Comment {
  id: string;
  thread_id: string;
  author_name: string;
  author_kind: AuthorKind;
  body: string;
  created_at: number;
}

export interface CommentThread {
  /** Admin only: anchor is absent from the published projection. */
  unpublished_anchor?: boolean;
  id: string;
  script_id: string;
  status: ThreadStatus;
  anchor_text: string;
  anchor_from: number;
  anchor_to: number;
  created_at: number;
  resolved_at: number | null;
  comments: Comment[];
}

export interface CreateThreadRequest {
  thread_id: string; // generated client-side so the editor can immediately apply a mark
  anchor_text: string;
  anchor_from: number;
  anchor_to: number;
  author_name: string;
  body: string;
  /** Version of the master document the selection was made against (PublicScript.doc_version). */
  doc_version?: number;
  /** When the page was showing an active suggestion draft: its id and edit_version at selection time; null or absent when master was shown. */
  suggestion_id?: string | null;
  suggestion_edit_version?: number | null;
}

export interface AddReplyRequest {
  thread_id: string;
  author_name: string;
  body: string;
}

// ---------- Per-script feature flags ----------

export interface ScriptFeatures {
  comments: boolean;
  suggestions: boolean;
  onboarding: boolean;
  onboarding_skip: boolean;
}

export const DEFAULT_FEATURES: ScriptFeatures = {
  comments: true,
  suggestions: true,
  onboarding: true,
  onboarding_skip: false,
};

export function parseFeatures(json: string | null | undefined): ScriptFeatures {
  if (!json) return { ...DEFAULT_FEATURES };
  try {
    const parsed = JSON.parse(json) as Partial<ScriptFeatures>;
    // Default-on flags only disable for explicit false, while onboarding_skip
    // defaults off and only enables for explicit true.
    return {
      comments: parsed.comments !== false,
      suggestions: parsed.suggestions !== false,
      onboarding: parsed.onboarding !== false,
      onboarding_skip: parsed.onboarding_skip === true,
    };
  } catch {
    return { ...DEFAULT_FEATURES };
  }
}

// ---------- Suggested edits ----------

export type SuggestionStatus = 'editing' | 'submitted' | 'accepted' | 'rejected';

export interface Suggestion {
  id: string;
  script_id: string;
  based_on_version: number;
  based_on_doc_json: string;
  doc_json: string;
  status: SuggestionStatus;
  author_name: string;
  note: string;
  rejection_note: string;
  created_at: number;
  updated_at: number;
  submitted_at: number | null;
  reviewed_at: number | null;
  /** Optimistic concurrency token incremented on every draft save/submit. */
  edit_version: number;
  /** Soft edit-lock (Feature L). `lock_token` is a cooperative per-browser id
   *  (not a secret); a lock is held while `lock_expires_at > now`. */
  lock_holder_name: string;
  lock_token: string;
  lock_expires_at: number;
}

/** Public review contract: documents and lock state, never lock credentials. */
export type PublicSuggestion = Pick<Suggestion,
  'id' | 'based_on_version' | 'based_on_doc_json' | 'doc_json' | 'status' | 'updated_at' |
  'edit_version' | 'lock_holder_name' | 'lock_expires_at'
> & {
  lock_present: boolean;
  lock_owned: boolean;
};

export interface SubmitSuggestionRequest {
  author_name: string;
  note: string;
  doc_json: string; // final state being submitted
  lock_token: string;
  expected_version: number;
}

export interface UpdateSuggestionRequest {
  doc_json: string;
  /** Cooperative edit-lock token + holder name (Feature L). */
  lock_token?: string;
  author_name?: string;
  expected_version?: number;
}

// ---------- Version history ----------

export type VersionReason =
  | 'manual'
  | 'status_change'
  | 'suggestion_pre_accept'
  | 'suggestion_accepted'
  // Publish revisions (2A): a new round, or a quiet update within the round.
  | 'publish_round'
  | 'publish_quiet';

export interface ScriptVersion {
  id: string;
  script_id: string;
  doc_version: number;
  title: string;
  reason: VersionReason;
  reason_note: string;
  created_at: number;
  /** Round the publish produced (publish_* rows only, else null). */
  published_round?: number | null;
  /** JSON array of confirmed publish bullets, also returned in version lists. */
  summary_json?: string | null;
}

export interface ScriptVersionFull extends ScriptVersion {
  doc_json: string;
}

export const VERSION_REASON_LABELS: Record<VersionReason, string> = {
  manual: 'Manual snapshot',
  status_change: 'Status change',
  suggestion_pre_accept: 'Before suggestion acceptance',
  suggestion_accepted: 'Suggestion accepted',
  publish_round: 'Published',
  publish_quiet: 'Quiet update',
};

// ---------- AI drafting ----------

/** One beat of a script — a paired audio/visual line. */
export interface AIDraftRow {
  audio: string;
  visuals: string;
}

/** A variant inside a multi-variant template (FB Ad Campaign). */
export interface AIDraftVariant {
  name: string;
  rows: AIDraftRow[];
}

/** A single video draft proposed by the AI. */
export interface AIDraftVideo {
  title: string;
  template_key: TemplateKey;
  overview: string;
  objective: string;
  rows?: AIDraftRow[];
  variants?: AIDraftVariant[];
  subject?: string;
  platform?: string;
  duration?: string;
  audience?: string;
  campaign_objective?: string;
  interview_questions?: string[];
}

/** Top-level structure returned by the AI: client name + the list of videos. */
export interface AIDraftResult {
  client_name: string;
  videos: AIDraftVideo[];
}

/** Per-proposal data the frontend needs to confirm creation. */
export interface AIDraftProposal {
  title: string;
  template_key: TemplateKey;
  overview: string;
  objective: string;
  /** Pre-built ProseMirror doc JSON. Pass as-is to createScript on confirm. */
  doc_json: string;
}

export interface AIDraftResponse {
  client_name: string;
  proposals: AIDraftProposal[];
}

// ---------- Recordings (Phase 2A) ----------

export type RecordingStatus =
  | 'recorded'
  | 'transcribing'
  | 'transcribed'
  | 'failed';

/** A single shoot-day audio take. Stored in R2; metadata in D1.
 *  Returned by the list endpoint with no transcript payload to keep the
 *  response small; use `RecordingDetail` (from the single-row endpoint)
 *  when you need the transcript body itself. */
export interface Recording {
  id: string;
  script_id: string;
  /** MIME type the take was recorded as (e.g. 'audio/webm', 'audio/mp4'). */
  mime_type: string;
  /** Total length in milliseconds. Null if the client failed to compute it. */
  duration_ms: number | null;
  /**
   * JSON-encoded array of marker objects: [{ q: <q_index>, t: <ms> }, ...].
   * One entry per "Next question" tap during the take. Stored as a string
   * so the client can pass it back to the server unchanged.
   */
  question_markers: string;
  status: RecordingStatus;
  recorded_at: number;
  transcribed_at: number | null;
  /** Paper-edit lifecycle visible in the recordings list so the panel
   *  can show "Analysing", "Ready", etc. inline without fetching the
   *  full transcript payload. */
  paper_edit_status: PaperEditStatus;
  paper_edit_generated_at: number | null;
  paper_edit_template_override: string | null;
}

/** Recording + transcript payload, returned by the single-row endpoint
 *  used by polling and transcript display. */
export interface RecordingDetail extends Recording {
  /** Raw JSON returned by the transcription model. Null until Whisper
   *  has completed successfully. Shape is documented by `StoredTranscript`
   *  in the worker's transcribe lib. */
  transcript_json: string | null;
  /** Last failure message when status='failed'; cleared on retry. */
  transcript_error: string | null;
  /** Paper-edit analysis state — separate from transcription status so a
   *  successful transcript can sit alongside a failed/in-flight analysis. */
  paper_edit_status: PaperEditStatus;
  /** JSON-encoded PaperEditAnalysis; null until generation succeeds. */
  paper_edit_json: string | null;
  paper_edit_error: string | null;
  paper_edit_generated_at: number | null;
  paper_edit_template_override: string | null;
}

/** Decoded marker shape after JSON.parse(question_markers). */
export interface QuestionMarker {
  /** 0-based index into the script's interview questions. */
  q: number;
  /** Milliseconds from the start of the recording. */
  t: number;
}

/** Decoded transcript payload after JSON.parse(transcript_json). Matches
 *  StoredTranscript on the worker side, kept here for the frontend's
 *  display logic. */
export interface DecodedTranscript {
  text: string;
  words?: Array<{ word: string; start: number; end: number }>;
  word_count?: number;
  vtt?: string;
  model: string;
}

// ---------- Paper edit (Phase 2C+) ----------

export type PaperEditStatus = 'pending' | 'analysing' | 'ready' | 'failed';

/** A quotable line extracted from the transcript with the AI's rationale
 *  for why it works in this context. Used for hooks, closings, and
 *  standout soundbites — same shape across all three. */
export interface PaperEditQuote {
  /** The exact (or near-exact) line from the transcript. */
  quote: string;
  /** One-sentence explanation of why this would work as a hook /
   *  closing / soundbite. Visible in the UI underneath the quote. */
  rationale: string;
}

/** Per-question summary block. `questionIndex` is null for any pre-roll
 *  content the speaker delivered before the first marker was tapped. */
export interface PaperEditKeyPoint {
  questionIndex: number | null;
  questionText: string;
  /** 3–6 bullets of what the talent actually said in this segment. */
  points: string[];
}

/** Full AI analysis stored in `recordings.paper_edit_json`. The shape is
 *  backend-agnostic — switching the underlying LLM (Workers AI ↔ Claude)
 *  doesn't require any schema change. */
export interface PaperEditAnalysis {
  hooks: PaperEditQuote[];
  keyPoints: PaperEditKeyPoint[];
  closings: PaperEditQuote[];
  soundbites: PaperEditQuote[];
  /** Identifier of the model that produced this analysis. Useful for
   *  the future "re-analyse with the newer model" button. */
  model: string;
  /** The template_key the analysis was tuned for. Equals the script's
   *  template_key unless the user supplied an override on the recording. */
  templateKey: TemplateKey;
}
